import type { BaseQueryFn, FetchArgs } from '@reduxjs/toolkit/query';
import { fetchBaseQuery } from '@reduxjs/toolkit/query';
import { Mutex } from 'async-mutex';
import { stringify } from 'qs';

import { EFeatureFlags, ENVS, HTTPStatusCode } from '@/shared/consts';
import {
  checkIsFlagEnabled,
  getCasdoorAccessToken,
  getCasdoorRefreshToken,
  getLMSAccessToken,
  getLMSRefreshToken,
  logoutUser,
  saveCasdoorTokens,
  saveLMSTokens,
} from '@/shared/helpers';

import { ENDPOINTS_WITH_CASDOOR_TOKEN, EXCLUDED_AUTHORIZATION_TOKEN_ENDPOINTS } from '../consts';
import type { ICasdoorAuthDataResponse, ILMSAuthDataResponse } from '../types';

import { casdoorMutex } from './casdoorMutex';

const lmsMutex = new Mutex();

const teachbaseBaseQuery = ({ baseUrl }: { baseUrl: string }) =>
  fetchBaseQuery({
    baseUrl,
    paramsSerializer: (params) => {
      if (params instanceof URLSearchParams) {
        return params.toString();
      }

      return stringify(params, {
        arrayFormat: 'brackets',
        skipNulls: true,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        filter: (_, value) => value || undefined,
      });
    },
    prepareHeaders: (headers, { endpoint }) => {
      headers.set('Content-Type', 'application/json');
      headers.set('Accept-Language', 'ru');

      const isExcludedEndpoint = EXCLUDED_AUTHORIZATION_TOKEN_ENDPOINTS.includes(endpoint);

      if (ENDPOINTS_WITH_CASDOOR_TOKEN.includes(endpoint)) {
        const casdoorAccessToken = getCasdoorAccessToken();

        if (casdoorAccessToken && !isExcludedEndpoint) {
          headers.set('Authorization', `Bearer ${casdoorAccessToken}`);
        }

        return headers;
      }

      const lmsAccessToken = getLMSAccessToken();

      if (lmsAccessToken && !isExcludedEndpoint) {
        headers.set('Authorization', `Bearer ${lmsAccessToken}`);
      }

      return headers;
    },
    credentials: checkIsFlagEnabled(EFeatureFlags.FetchCredentials) ? 'include' : 'same-origin',
  });

export const teachbaseBaseQueryWithReauth =
  ({ baseUrl }: { baseUrl: string }): BaseQueryFn =>
  async (args, api, extraOptions) => {
    const baseQuery = teachbaseBaseQuery({ baseUrl });

    const fetchArgs = args as string | FetchArgs;

    const isCasdoorTokenRequest = ENDPOINTS_WITH_CASDOOR_TOKEN.includes(api.endpoint);

    const finalMutex = isCasdoorTokenRequest ? casdoorMutex : lmsMutex;

    const refreshCasdoorTokens = async () => {
      const casdoorRefreshToken = getCasdoorRefreshToken();

      if (!casdoorRefreshToken) {
        logoutUser();

        return null;
      }

      const result = await baseQuery(
        {
          method: 'POST',
          url: `${ENVS.CASDOOR_API_URL}/login/oauth/refresh_token`,
          params: {
            grant_type: 'refresh_token',
            refresh_token: casdoorRefreshToken,
            scope: 'openid+profile+email',
            client_id: ENVS.CASDOOR_CLIENT_ID,
          },
        },
        { ...api, endpoint: 'refresh_token' },
        extraOptions,
      );

      if (!result.data) {
        logoutUser();

        return null;
      }

      const data = result.data as ICasdoorAuthDataResponse;

      saveCasdoorTokens({
        casdoorAccessToken: data.access_token,
        casdoorRefreshToken: data.refresh_token,
      });

      return data;
    };

    const refreshLMSTokens = async () => {
      const lmsRefreshToken = getLMSRefreshToken();

      if (!lmsRefreshToken) {
        logoutUser();

        return null;
      }

      const result = await baseQuery(
        {
          method: 'POST',
          url: `${ENVS.LMS_URL}/oauth/token`,
          params: {
            grant_type: 'refresh_token',
            refresh_token: lmsRefreshToken,
          },
        },
        { ...api, endpoint: 'refresh_token' },
        extraOptions,
      );

      if (!result.data) {
        logoutUser();

        return null;
      }

      const { access_token, refresh_token } = result.data as ILMSAuthDataResponse;

      saveLMSTokens({ lmsAccessToken: access_token, lmsRefreshToken: refresh_token });

      return result.data;
    };

    await finalMutex.waitForUnlock();

    let result = await baseQuery(fetchArgs, api, extraOptions);

    const isTokenOrSessionExpired = result.error?.status === 0;

    if (result.error?.status !== HTTPStatusCode.UnAuthorized && !isTokenOrSessionExpired) {
      return result;
    }

    if (finalMutex.isLocked()) {
      await finalMutex.waitForUnlock();

      return baseQuery(fetchArgs, api, extraOptions);
    }

    const release = await finalMutex.acquire();

    try {
      /* Запросам которым нужен токен Casdoor запрашиваем его, остальным запросам токен teachbase */
      const authData = isCasdoorTokenRequest ? await refreshCasdoorTokens() : await refreshLMSTokens();

      if (!authData) {
        return result;
      }

      result = await baseQuery(fetchArgs, api, extraOptions);
    } catch {
      logoutUser();
    } finally {
      release();
    }

    return result;
  };
